import React, { useMemo } from "react";
import {
    GENERAL_LEDGER_FILTER_KEYS,
    GENERAL_LEDGER_FILTER_TREE,
    GeneralLedgerFilterTarget,
    getGeneralLedgerRoleLabel,
    readGeneralLedgerRoles,
    readGeneralLedgerTypes,
} from "@common/helpers/accounting/general-ledger-filter";
import { FiltersTypes } from "@common/types/filters/general";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { FilterLabel } from "@components/pageHeader/filters/ActiveFilters/FilterLabel";

interface Props {
    filters: FiltersTypes;
    target: GeneralLedgerFilterTarget;
}

export const GeneralLedgerFilterLabel: React.FC<Props> = ({
    filters,
    target,
}) => {
    const keys = GENERAL_LEDGER_FILTER_KEYS[target];
    const types = readGeneralLedgerTypes(filters[keys.types]);
    const roles = readGeneralLedgerRoles(filters[keys.roles]);
    const count = types.length + roles.length;

    const tooltipLines = useMemo(() => {
        if (count <= 1) {
            return null;
        }

        const lines = GENERAL_LEDGER_FILTER_TREE.flatMap((node) => {
            const isTypeSelected = types.includes(node.type);
            const selectedPurposes = node.purposes.filter((purpose) =>
                roles.includes(purpose.role),
            );
            if (!isTypeSelected && selectedPurposes.length === 0) {
                return [];
            }
            return [
                <div key={node.type}>
                    <strong>{node.type}</strong>
                    {isTypeSelected
                        ? null
                        : selectedPurposes.map((purpose) => (
                              <div
                                  key={purpose.role}
                                  style={{ paddingLeft: "var(--space-8)" }}
                              >
                                  {purpose.label}
                              </div>
                          ))}
                </div>,
            ];
        });

        return lines.length > 0 ? lines : null;
    }, [roles, types, count]);

    if (count === 0) {
        return null;
    }

    let labelText = `${count} selected`;
    if (count === 1 && types.length === 1) {
        labelText = types[0];
    } else if (count === 1 && roles[0]) {
        labelText = getGeneralLedgerRoleLabel(roles[0]);
    }

    if (!tooltipLines) {
        return <FilterLabel label={labelText} />;
    }

    return (
        <Tooltip content={tooltipLines}>
            <span>
                <FilterLabel label={labelText} />
            </span>
        </Tooltip>
    );
};
